import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MonetizationOnOutlined } from '@mui/icons-material';
import * as Cons from 'data/Consignment';
import Page from 'components/UI/Page';
import Breadcrumbs from 'components/UI/Breadcrumb';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import Modal from 'components/UI/Modal';
import { renderToast, toast } from 'components/UI/ToastNotification';
import ConfirmationModal from 'components/UI/ConfimationModal';
import WarningBanner from 'components/UI/WarningBanner';
import { useModalState } from 'hooks/useAddonModalState';
import Icon from 'view/Icon';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { navigate } from 'hooks/useHookRouterCompat';
import { loadConsignment, applyOrderChanges, discardOrderEdits, updateState } from 'requests/consignment';
import { ORDER_SENT_TO_OEM, AWAITS_EDIT_APPROVAL } from 'constants/consignmentStates';
import { CONSIGNMENT_TYPES, TEXT_LABELS } from 'constants/order';
import { useCountries } from 'hooks/useCountries';
import { getCurrencyIsoByCountry, getCurrencySymbol } from 'utils/country';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { FontWeight } from 'lib/styles';
import CatalogLookupResult from 'data/CatalogLookupResult';
import CatalogItemLookup from './CatalogItemLookup';
import EditOrderItemsTable, { AddedCatalogItem, EditOrderItemsTableHandle } from './EditOrderItemsTable';
import EditOrderFooter from './EditOrderFooter';
import Button from 'components/UI/Button';
import { EDIT_ORDER_ACTIONS, EDIT_ORDER_RESULTS } from 'constants/product';

const HeadingInformation = styled.h1`
  font-size: 32px;
  line-height: 36px;
  font-weight: ${FontWeight.medium};
  margin: 0;
`;

const HeadingSubtitle = styled.span`
  color: ${Text.secondary};
  font-size: 14px;
  line-height: 20px;
`;

const StyledEditOrderIcon = styled(MonetizationOnOutlined) <any>`
  font-size: 44px !important;
  color: ${Main.brandDark} !important;
`;

interface Props {
  consignmentId: string;
};

/**
 * Edit order screen (routed at /orders/order-details/:id/edit-order, behind the
 * editOrder feature flag). Opening it does NOT change the consignment state — it stays
 * ORDER_SENT_TO_OEM for the whole edit session; the EDIT_ORDER snapshot tracks the
 * session instead. Cancelling always discards the session and restores the pre-edit
 * state, sending the user back to the order details screen.
 */
const EditOrderContainer = ({ consignmentId }: Props) => {
  const { user } = useAuth();
  const { countries } = useCountries();
  const [consignmentDetails, setConsignmentDetails] = useState<Cons.ConsignmentDetails>(Cons.emptyConsignmentDetails);
  const [addedItems, setAddedItems] = useState<AddedCatalogItem[]>([]);
  const [removedEntryIds, setRemovedEntryIds] = useState<number[]>([]);
  // Persisted-REMOVED entries restored in this session — displayed as live rows again;
  // the DB keeps the REMOVED marker until the next apply un-marks it
  const [restoredRemovedEntryIds, setRestoredRemovedEntryIds] = useState<number[]>([]);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [editOrderResult, setEditOrderResult] = useState<string | null>(null);
  const [hasDraftEdits, setHasDraftEdits] = useState(false);
  // A row with an invalid unit price blocks the apply — the error is already on screen
  const [hasInvalidEdits, setHasInvalidEdits] = useState(false);
  const nextAddedItemId = useRef(-1);
  const itemsTableRef = useRef<EditOrderItemsTableHandle>(null);
  const { modalState, showModal, hideModal } = useModalState(['cancel']);

  const orderNumber = consignmentId.slice(4);
  const orderDetailsPath = `/orders/order-details/${orderNumber}`;

  const consignmentReq = useAPI({
    deferFn: loadConsignment,
    onResolve: (result: Cons.ConsignmentDetails) => {
      // Edit order is out of scope for rentals, subscriptions and affiliate programs, and
      // is only offered while the order is still in New status - block direct navigation.
      if (!result.consignmentId || result.consignmentType !== CONSIGNMENT_TYPES.PURCHASE || result.state !== ORDER_SENT_TO_OEM) {
        navigate(orderDetailsPath, true);
        return;
      }

      setConsignmentDetails(result);
      setEditOrderResult(result.editOrderResult!);
      setIsRecalculating(result.editOrderResult === 'PENDING');
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message);
      navigate(orderDetailsPath, true);
    },
    debugLabel: 'editOrderConsignmentRequest'
  });

  const consignmentReqRef = useRef(consignmentReq);
  useEffect(() => {
    consignmentReqRef.current = consignmentReq;
  });

  useEffect(() => {
    consignmentReqRef.current.run(consignmentId, user?.vendorId);
  }, [consignmentId, user?.vendorId]);

  const applyChangesReq = useAPI({
    deferFn: applyOrderChanges,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, 'Order changes applied');
      setAddedItems([]);
      setRemovedEntryIds([]);
      setRestoredRemovedEntryIds([]);
      setIsRecalculating(false);
      setEditOrderResult('SUCCESS');
      setHasDraftEdits(false);

      consignmentReq.run(consignmentId, user?.vendorId);
    },
    // Refetch so the FAILED snapshot status (shown as a banner) is picked up
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err.message);
      setIsRecalculating(false);
      setEditOrderResult('FAILED');
      consignmentReq.run(consignmentId, user?.vendorId);
    }
  });

  const discardReq = useAPI({
    deferFn: discardOrderEdits,
    onResolve: () => { exitEditing(); },
    onReject: () => { exitEditing(); },
  });

  // Submits the applied changes to the buyer - moves the consignment to
  // AWAITS_EDIT_APPROVAL; OrderDetails then renders the awaiting-approval view
  const sendForApprovalReq = useAPI({
    deferFn: updateState,
    onResolve: () => {
      renderToast(toast.TYPE.SUCCESS, TEXT_LABELS.SEND_FOR_APPROVAL_SUCCESS);
      navigate(orderDetailsPath);
    },
    onReject: () => {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.SEND_FOR_APPROVAL_FAILED);
    }
  });

  const currencyIso = useMemo(() => {
    const countryIso = countries.find(country => country.id === consignmentDetails.countryId)?.countryIso;

    return getCurrencyIsoByCountry(countryIso!);
  }, [countries, consignmentDetails.countryId]);

  const crumbs = useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'Orders', path: '/orders' },
    { name: `Split order # ${consignmentDetails.order.orderId}`, path: `/orders/split-order-details/${consignmentDetails.order.orderId}` },
    { name: `Delivery order # ${orderNumber}`, path: orderDetailsPath },
    { name: TEXT_LABELS.EDIT_ORDER, path: `${orderDetailsPath}/edit-order` }
  ], [consignmentDetails.order.orderId, orderNumber, orderDetailsPath]);

  // Entries restored in this session have the REMOVED marker overridden on screen, so
  // the table renders them as ordinary live rows (editable, included in the apply payload)
  const displayEntries = useMemo(() => consignmentDetails.consignmentEntries.map(entry =>
    restoredRemovedEntryIds.includes(entry.id) ? { ...entry, modificationType: undefined } : entry),
    [consignmentDetails.consignmentEntries, restoredRemovedEntryIds]);

  // Session-removed entries plus persisted-REMOVED entries not yet restored — their SKUs
  // cannot be re-added through the lookup (restored ones go back to being blocked by
  // existingSkus as "already added")
  const removedSkus = useMemo(() => consignmentDetails.consignmentEntries
    .filter(entry => removedEntryIds.includes(entry.id)
      || (entry.modificationType === EDIT_ORDER_ACTIONS.REMOVED && !restoredRemovedEntryIds.includes(entry.id)))
    .map(entry => entry.sku), [consignmentDetails.consignmentEntries, removedEntryIds, restoredRemovedEntryIds]);

  const existingSkus = useMemo(() => [
    ...displayEntries.filter(entry => !removedEntryIds.includes(entry.id)).map(entry => entry.sku),
    ...addedItems.map(item => item.sku)
  ], [displayEntries, removedEntryIds, addedItems]);

  const hasUnsavedChanges = addedItems.length > 0 || removedEntryIds.length > 0 || restoredRemovedEntryIds.length > 0 || hasDraftEdits;

  // Same readiness condition as the footer's send action — once changes are applied and
  // the approval request is still required, the seller must either send it or cancel
  // (discard); backing out of the screen silently is not allowed
  const canSendForApproval = consignmentDetails.editOrderResult === EDIT_ORDER_RESULTS.SUCCESS && !hasUnsavedChanges;

  // A failed recalculation replaces the "not sent for approval" banner
  const showFailedBanner = editOrderResult === EDIT_ORDER_RESULTS.FAILED;
  const showUnsentChangesBanner = !showFailedBanner && hasUnsavedChanges;

  // Leaving the edit screen means ending the session — back to the order details page
  const exitEditing = () => navigate(orderDetailsPath);

  // Cancelling always discards the edit session (restores the pre-edit state); confirm
  // first only when there is something to lose
  const requestCancel = () => {
    showModal('cancel');
  };

  const handleAddItem = (item: CatalogLookupResult) => {
    setAddedItems(current => [...current, {
      id: nextAddedItemId.current--,
      sku: item.sku,
      code: item.code,
      materialNumber: item.materialNumber,
      productName: item.productName,
      basePrice: item.basePrice,
      productImages: item.productImages
    }]);
  };

  const handleRemoveRow = (id: number, isAddition: boolean) => {
    if (isAddition) {
      setAddedItems(current => current.filter(item => item.id !== id));
    } else {
      setRemovedEntryIds(current => [...current, id]);
    }
  };

  // Brings back a removed row: un-marks a session removal and, for a persisted-REMOVED
  // entry, flips it back to a live row locally (the DB stays REMOVED until the next apply)
  const handleRestoreRow = (id: number) => {
    setRemovedEntryIds(current => current.filter(entryId => entryId !== id));
    const entry = consignmentDetails.consignmentEntries.find(e => e.id === id);
    if (entry?.modificationType === EDIT_ORDER_ACTIONS.REMOVED) {
      setRestoredRemovedEntryIds(current => current.includes(id) ? current : [...current, id]);
    }
  };

  const handleApplyChanges = () => {
    const existing = itemsTableRef.current?.getExistingEntryEdits() ?? [];
    const added = itemsTableRef.current?.getAddedItemsWithChanges() ?? [];

    if (existing.length === 0 && added.length === 0) {
      renderToast(toast.TYPE.ERROR, TEXT_LABELS.CANNOT_REMOVE_ALL_ITEMS);
      return;
    }

    applyChangesReq.run(consignmentId, [...existing, ...added]);
    setIsRecalculating(true);
  };


  // Full-screen loader whenever the consignment is being loaded — the initial fetch and
  // the background re-fetches (e.g. after an apply) both block the screen
  if (consignmentReq.isLoading || !consignmentDetails.consignmentId) {
    return <Page><LoadingIndicator center /></Page>;
  }

  return (
    <Page bgColor={Background.light}>
      <Breadcrumbs containerPadding="0 0 24px 8px" crumbs={crumbs} />
      <FlexboxContainer justifyContent="space-between" alignItems="flex-start" margin={`0 ${Space.sp2x} ${Space.sp3x}`}>
        <FlexboxContainer alignItems="flex-start" gap={Space.sp2x}>
          <StyledEditOrderIcon />
          <FlexboxContainer flexDirection="column" gap={Space.spHalf}>
            <HeadingInformation>
              {TEXT_LABELS.EDIT_ORDER} &nbsp; # {orderNumber}
            </HeadingInformation>
            <HeadingSubtitle className="base">{TEXT_LABELS.EDIT_ORDER_SUBTITLE}</HeadingSubtitle>
          </FlexboxContainer>
        </FlexboxContainer>
          <Button
            isGhost
            color={Text.primary}
            borderColor={Border.main}
            padding={`0 ${Space.sp2x}`}
            onClick={requestCancel}
            disabled={canSendForApproval}
            leftIcon={(size: string) => <Icon name="arrow-left" size={parseInt(size)} />}
          >
            {TEXT_LABELS.BACK_TO_ORDER_DETAILS}
          </Button>
      </FlexboxContainer>
      {showFailedBanner && (
        <WarningBanner
          type="error"
          fontSize="14px"
          margin={`0 ${Space.sp2x} ${Space.sp2x}`}
          message={TEXT_LABELS.ORDER_RECALCULATION_FAILED}
        />
      )}
      {
        sendForApprovalReq.isLoading && (
          <WarningBanner
            type="warning"
            fontSize="14px"
            margin={`0 ${Space.sp2x} ${Space.sp2x}`}
            message={TEXT_LABELS.AWAITS_EDIT_ORDER_PENDING}
          />
        )
      }
      {showUnsentChangesBanner && (
        <WarningBanner
          type="warning"
          fontSize="14px"
          margin={`0 ${Space.sp2x} ${Space.sp2x}`}
          message={TEXT_LABELS.CHANGES_NOT_SENT_WARNING}
        />
      )}
      <CatalogItemLookup
        vendorId={consignmentDetails.vendorId}
        businessUnitId={consignmentDetails.businessUnitId}
        countryId={consignmentDetails.countryId}
        warehouseId={consignmentDetails.warehouse?.id}
        currencyIso={currencyIso}
        currencySymbol={getCurrencySymbol(currencyIso)}
        existingSkus={existingSkus}
        removedSkus={removedSkus}
        onAddItem={handleAddItem}
      />
      <EditOrderItemsTable
        ref={itemsTableRef}
        consignmentEntries={displayEntries}
        addedItems={addedItems}
        removedEntryIds={removedEntryIds}
        onRemoveRow={handleRemoveRow}
        onRestoreRow={handleRestoreRow}
        onDraftEditsChange={setHasDraftEdits}
        onValidityChange={setHasInvalidEdits}
        currencyIso={currencyIso}
        currencySymbol={getCurrencySymbol(currencyIso)}
        orderTotal={consignmentDetails.totalPriceWithTax || consignmentDetails.subTotal}
      />
      <EditOrderFooter
        onCancel={requestCancel}
        onApplyChanges={handleApplyChanges}
        isApplying={applyChangesReq.isLoading}
        isCancelling={discardReq.isLoading}
        hasAppliedChanges={consignmentDetails.editOrderResult === EDIT_ORDER_RESULTS.SUCCESS}
        onSendForApproval={() => sendForApprovalReq.run(consignmentId, AWAITS_EDIT_APPROVAL)}
        isSending={sendForApprovalReq.isLoading}
        hasUnsavedChanges={hasUnsavedChanges}
        hasInvalidEdits={hasInvalidEdits}
      />
      <ConfirmationModal
        show={modalState.cancel}
        onClose={() => hideModal('cancel')}
        onConfirm={() => { hideModal('cancel'); discardReq.run(consignmentId); }}
        title={TEXT_LABELS.CANCEL_EDITING_CONFIRM_TITLE}
        message={TEXT_LABELS.CANCEL_EDITING_CONFIRM_MESSAGE}
      />
      <Modal
        showModal={isRecalculating || editOrderResult === EDIT_ORDER_RESULTS.PENDING}
        showCloseIcon={false}
        showButtons={false}
        onClose={() => { }}
        title={TEXT_LABELS.RECALCULATING_ORDER_TOTALS}
      >
        <FlexboxContainer flexDirection="column" alignItems="center" gap={Space.spBase}>
          <LoadingIndicator />
          <span className="base">{TEXT_LABELS.RECALCULATING_ORDER_TOTALS_MESSAGE}</span>
          <strong className="base">{TEXT_LABELS.RECALCULATING_ORDER_TOTALS_WARNING}</strong>
        </FlexboxContainer>
      </Modal>
    </Page>
  );
};

export default EditOrderContainer;
